import { forwardRef } from 'react';
import classNames from 'classnames';
import './Text.scss';

export type TextSize = 'large' | 'medium' | 'small' | 'xs' | '2xs' | '3xs' | '4xs';

export const TEXT_ALIGNS = ['start', 'end', 'center', 'left', 'right'] as const;

export type TextAlign = (typeof TEXT_ALIGNS)[number];

export const TEXT_HEADINGS = [
  'display-01',
  'heading-01',
  'heading-02',
  'heading-03',
  'heading-04',
  'subtitle-01',
  'subtitle-02',
] as const;

export type TextHeading = (typeof TEXT_HEADINGS)[number];

export const TEXT_BODIES = ['body-01', 'body-02', 'metadata', 'caption'] as const;

export type TextBody = (typeof TEXT_BODIES)[number];

export type TextLink = 'link';

export type TextVariant = TextHeading | TextBody | TextLink | 'kids' | 'metadata';

export type TextWeight = 'black' | 'bold' | 'semibold' | 'regular' | 'light';

export type TextColor =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'critical'
  | 'success'
  | 'info'
  | 'links'
  | 'disabled'
  | 'label-primary'
  | 'primary-interactive'
  | 'foreverwhite';

export type TextOverflowWrap = 'normal' | 'break-word';

export interface TextProps {
  id?: string;
  children: React.ReactNode;
  className?: string;
  component?: React.ElementType;
  size?: TextSize;
  variant?: TextVariant;
  weight?: TextWeight;
  color?: TextColor;
  align?: TextAlign;
  lineClamp?: number | true;
  overflowWrap?: TextOverflowWrap;
  tabIndex?: number;
}

/**
 * Text Component - Should not be used in isolation.
 * Was initially created to be used for all text elements but this is no longer encouraged.
 * **Please use Heading, Body, or Link components instead**.
 * @param id - A unique identifier for the element.
 * @param children - Any React.ReactNode component.
 * @param className - Any additional classnames.
 * @param component - Any HTML element tag.
 * @param size - Defines the size of the text.
 * @param variant - Choose between any of the Typography available variants.
 * @param weight - Defines the weight of the text.
 * @param color - Defines the color of the text.
 * @param align - Defines the alignment of the text.
 * @param lineClamp - Visually truncate the text to the specified number of lines.
 * @param overflowWrap - How truncation is handled when text overflows the line.
 * @param tabIndex - Defines its position in the navigation order.
 */
export const Text = forwardRef<HTMLElement, TextProps>(
  (
    {
      id,
      children,
      className,
      component: Component = 'span',
      size = 'medium',
      variant = 'body-01',
      weight = 'regular',
      color,
      align,
      lineClamp,
      overflowWrap,
      tabIndex,
      ...rest
    },
    ref,
  ) => (
    <Component
      id={id}
      ref={ref}
      className={classNames(
        'vds-typography',
        {
          [`vds-typography-${variant}-${weight}-${size}`]: variant && weight && size,
          [`vds-typography-color--${color}`]: color,
          'vds-typography-line-clamp': lineClamp,
        },
        className,
      )}
      tabIndex={tabIndex ?? undefined}
      style={{ textAlign: align, WebkitLineClamp: lineClamp ? lineClamp : null, overflowWrap }}
      {...rest}>
      {children}
    </Component>
  ),
);
